import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";
import useSeo from "../../hooks/useSeo";
import { PRODUCT_SEO } from "../../data/seo";
import { catalogue as fetchCatalogue } from "../../data/paymentsApi";

/*
 * What Doorstep Data costs.
 *
 * WHY A PAGE OF ITS OWN, AND WHY NOT UNDER /account. The only place a price
 * appeared was the checkout at /account/buy, which is behind a sign-in and
 * is served `noindex` by the product nginx block - deliberately, because a
 * checkout has no business in a search index. The effect was that nobody
 * could find out what a report cost without first making an account, which
 * is the wrong way round: the price is an objection to answer before the
 * sign-up, not after it.
 *
 * So this is a top-level, indexable path, and that is the expensive part:
 * the product nginx block refuses by default, so /pricing had to be added
 * to it and installed by hand (CLAUDE.md 6a, 6f and 9). A deploy does not
 * install nginx config. If this page 404s on doorstepdata.co.uk, that step
 * is what is missing - the build is fine.
 *
 * THE PRICES ARE FETCHED, NOT WRITTEN DOWN HERE. api/data/entitlements.js
 * holds them, the checkout reads them through /payments/catalogue, and so
 * does this page. A fourth copy is how one surface ends up advertising
 * 3.99 for what another charges 4.99 for. The cost is that the figures
 * arrive a moment after the page does - which is why the renderer puts the
 * real numbers into the served HTML as well, for anybody (and any crawler)
 * who never runs the bundle. See staticBody() in api/render.js.
 */

const Hero = styled.section`
    background: #0f4c5c;
    color: #fff;
    padding: 3.5rem 0 3rem;
`;
const HeroInner = styled.div`
    max-width: 44rem;

    h1 {
        margin: 0 0 0.75rem;
        font-size: clamp(1.8rem, 4vw, 2.6rem);
        line-height: 1.15;
    }
    p {
        margin: 0;
        font-size: 1.05rem;
        line-height: 1.6;
        opacity: 0.92;
    }
`;

const Grid = styled.div`
    display: grid;
    gap: 1.25rem;
    grid-template-columns: repeat(auto-fit, minmax(min(16rem, 100%), 1fr));
    margin: 2.5rem 0 1rem;
`;

/* The free tier is a card like the others on purpose. Setting it apart as a
   footnote would hide the thing most visitors should leave having used. */
const Card = styled.div`
    border: 1px solid ${(props) => (props.$lead ? "#0f4c5c" : "#e6e9ee")};
    border-radius: 6px;
    padding: 1.5rem 1.35rem;
    display: flex;
    flex-direction: column;

    h2 {
        margin: 0 0 0.25rem;
        font-size: 1.05rem;
    }
`;
const Amount = styled.p`
    margin: 0.35rem 0 0.1rem;
    font-size: 1.9rem;
    font-weight: 700;
    line-height: 1.1;
`;
const Per = styled.span`
    font-size: 0.85rem;
    font-weight: 400;
    opacity: 0.7;
`;
const Blurb = styled.p`
    margin: 0.6rem 0 0;
    font-size: 0.9rem;
    line-height: 1.6;
    opacity: 0.85;
    flex: 1 1 auto;
`;
const Action = styled(Link)`
    display: inline-block;
    margin-top: 1.1rem;
    padding: 0.6rem 1rem;
    border-radius: 4px;
    background: #0f4c5c;
    color: #fff;
    font-size: 0.9rem;
    text-decoration: none;
    text-align: center;

    &:hover {
        color: #fff;
        background: #0b3b47;
    }
`;

const Body = styled.div`
    max-width: 44rem;
    padding-bottom: 3rem;

    h2 {
        margin: 2.25rem 0 0.6rem;
        font-size: 1.15rem;
    }
    p,
    li {
        line-height: 1.7;
        font-size: 0.95rem;
    }
    ul {
        padding-left: 1.1rem;
    }
`;

const Note = styled.p`
    font-size: 0.85rem;
    opacity: 0.75;
`;

/**
 * The catalogue, fetched once.
 *
 * The same hook shape as the checkout's. A failure is not fatal here: the
 * page still explains what is free and what is sold, and says it could not
 * reach the prices rather than showing a stale guess at them.
 */
function useCatalogue() {
    const [state, setState] = useState({ loading: true, items: null, error: null });

    useEffect(() => {
        let alive = true;
        fetchCatalogue()
            .then(
                (data) =>
                    alive &&
                    setState({
                        loading: false,
                        items: Array.isArray(data && data.items) ? data.items : [],
                        error: null,
                    })
            )
            .catch(
                (e) =>
                    alive &&
                    setState({ loading: false, items: null, error: e.message || "unavailable" })
            );
        return () => {
            alive = false;
        };
    }, []);

    return state;
}

/** "£4.99", from the decimal string the API sends. */
function money(amount) {
    return "£" + String(amount);
}

export default function Pricing() {
    useSeo(PRODUCT_SEO["/pricing"]);
    const { loading, items, error } = useCatalogue();

    const find = (sku) => (items || []).find((i) => i.sku === sku) || null;
    const single = find("single");
    const bundle = find("bundle3");
    const monthly = find("monthly");
    const annual = find("annual");

    /* One placeholder, used wherever a figure has not arrived. Showing a
       number we invented here would defeat the point of fetching them. */
    const price = (item) => (item ? money(item.amount) : loading ? "…" : "—");

    return (
        <>
            <SiteNav />

            <Hero>
                <Container>
                    <HeroInner>
                        <h1>What it costs</h1>
                        <p>
                            Postcode reports are free and always will be. A full report on one
                            address — its band, its certificate, its own sold prices and an
                            instant valuation — is what is paid for.
                        </p>
                    </HeroInner>
                </Container>
            </Hero>

            <Container>
                <Grid>
                    <Card $lead>
                        <h2>Free</h2>
                        <Amount>
                            £0
                        </Amount>
                        <Blurb>
                            Every postcode report, for ever, with no account. Plus your first
                            full property report free when you make one — council tax, energy
                            performance and sold prices for that address.
                        </Blurb>
                        <Action to="/property-report">Run a report</Action>
                    </Card>

                    <Card>
                        <h2>One report</h2>
                        <Amount>{price(single)}</Amount>
                        <Blurb>
                            One full property report, including the instant valuation. Yours to
                            re-open whenever you like — a report you have paid for never expires.
                        </Blurb>
                        <Action to="/account/buy">Buy one</Action>
                    </Card>

                    <Card>
                        <h2>Three reports</h2>
                        <Amount>{price(bundle)}</Amount>
                        <Blurb>
                            Three full reports, used whenever you want them. Credits are not
                            tied to a property until you spend one, so you can change your mind
                            about which house you meant.
                        </Blurb>
                        <Action to="/account/buy">Buy three</Action>
                    </Card>

                    <Card>
                        <h2>Monthly</h2>
                        <Amount>
                            {price(monthly)} <Per>a month</Per>
                        </Amount>
                        <Blurb>
                            Ten properties a month, on the website and in the Android app. Cancel
                            whenever you like; reports you have already opened stay open.
                        </Blurb>
                        <Action to="/account/plan">See the plan</Action>
                    </Card>
                </Grid>

                <Body>
                    <Note>
                        All prices include VAT. {annual ? (
                            <>
                                Paying yearly is {money(annual.amount)} for the same ten a month —
                                two months free against the monthly rate.
                            </>
                        ) : null}
                        {error ? " Prices could not be loaded just now; the checkout has them." : null}
                    </Note>

                    <h2>What is always free</h2>
                    <ul>
                        <li>
                            <strong>Postcode reports.</strong> Council tax, schools, crime,
                            transport, shops, parks, broadband and sold prices for an area. No
                            account, no limit.
                        </li>
                        <li>
                            <strong>House prices</strong> for every postcode district in England
                            and Wales.
                        </li>
                        <li>
                            <strong>Measuring and photography in the app.</strong> Floor plans and
                            room photographs run entirely on your phone and are not charged for.
                        </li>
                        <li>
                            <strong>Your first full property report</strong>, once, when you make
                            an account.
                        </li>
                    </ul>

                    <h2>What you are paying for</h2>
                    <p>
                        A property report is about one home rather than an area. It needs that
                        address's energy certificate, its council tax band and its floor area, and
                        it is the only thing here that costs us money per property — the solar
                        estimate and the environmental risk screening are bought per address, and
                        the valuation is worked out on a server.
                    </p>
                    <p>
                        A report you have paid for stays yours. Re-opening it is free and does not
                        count again, after a subscription lapses as much as during one.
                    </p>

                    <h2>The app</h2>
                    <p>
                        The Android app is free to download and reads every report this site does.
                        A subscription covers both — buy it in either place and it works in the
                        other, because what you have bought belongs to your account rather than to
                        a device.
                    </p>

                    <Note>
                        Questions about a charge, or a figure that looks wrong?{" "}
                        <Link to="/contact">Get in touch</Link>.
                    </Note>
                </Body>
            </Container>

            <SiteFooter />
        </>
    );
}
